import React, { useEffect, useState } from "react";
import { Card, Row, Col, Button, Select, DatePicker, Space } from "antd";
import { SearchOutlined, ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";

import { selectAuthInfo } from "../../Auth/authSelectors";
import { isAdmin } from "../../../lib/functions";
import { fetchUsers } from "../../../store/commonThunks/lookups";
import { fetchTeamsApi, fetchMyTeammatesApi } from "../../../apiservice/crm";

const { RangePicker } = DatePicker;

// Shared date-range + employee/team filter row used by every CRM report
// except Lead Status (which keeps its own existing CrmReportFilters). Admins
// get full Employee/Team pickers; non-admins only see an Employee picker
// scoped to themselves + their own team, mirroring the CRM Teams
// self-service model used for lead reassignment.
const ReportFilterBar = ({ filters, onChange, onApply, onReset, loading, extra }) => {
  const dispatch = useDispatch();
  const authInfo = useSelector(selectAuthInfo);
  const adminUser = isAdmin(authInfo);
  const users = useSelector((s) => s.lookups.users || []);
  const self = authInfo?.userInfo || {};

  const [teams, setTeams] = useState([]);
  const [myTeam, setMyTeam] = useState(null);

  useEffect(() => {
    if (adminUser) {
      if (!users.length) dispatch(fetchUsers());
      fetchTeamsApi().then((res) => setTeams(res.data || [])).catch(() => setTeams([]));
    } else {
      fetchMyTeammatesApi().then((res) => setMyTeam(res.data || null)).catch(() => setMyTeam(null));
    }
  }, [adminUser, dispatch, users.length]);

  const showEmployeeFilter = adminUser || !!myTeam?.teamId;
  const employeeOptions = adminUser
    ? users.map((u) => ({ label: u.full_name, value: u.id }))
    : myTeam?.teamId
    ? [
        { label: `${self.full_name || "Me"} (Me)`, value: self.user_id },
        ...(myTeam.members || []).map((m) => ({ label: m.full_name, value: m.id })),
      ]
    : [];

  return (
    <Card className="mb-4 shadow-md rounded-xl">
      <Row gutter={[16, 16]} align="bottom">
        <Col xs={24} sm={12} md={8} lg={6}>
          <label>Date range</label>
          <RangePicker
            style={{ width: "100%" }}
            size="large"
            value={filters.fromDate && filters.toDate ? [dayjs(filters.fromDate), dayjs(filters.toDate)] : null}
            onChange={(dates) =>
              onChange({
                fromDate: dates?.[0] ? dates[0].format("YYYY-MM-DD") : null,
                toDate: dates?.[1] ? dates[1].format("YYYY-MM-DD") : null,
              })
            }
          />
        </Col>

        {showEmployeeFilter && (
          <Col xs={24} sm={12} md={8} lg={5}>
            <label>Employee</label>
            <Select
              allowClear
              placeholder="All"
              style={{ width: "100%" }}
              size="large"
              value={filters.employee_id || undefined}
              onChange={(v) => onChange({ employee_id: v || null, team_id: null })}
              options={employeeOptions}
            />
          </Col>
        )}

        {adminUser && (
          <Col xs={24} sm={12} md={8} lg={5}>
            <label>Team</label>
            <Select
              allowClear
              placeholder="All"
              style={{ width: "100%" }}
              size="large"
              value={filters.team_id || undefined}
              onChange={(v) => onChange({ team_id: v || null, employee_id: null })}
              options={teams.map((t) => ({ label: t.name, value: t.id }))}
            />
          </Col>
        )}

        {extra}

        <Col xs={24} sm={12} md={8} lg={4}>
          <Space>
            <Button type="primary" icon={<SearchOutlined />} size="large" loading={loading} onClick={onApply}>
              Apply
            </Button>
            <Button icon={<ReloadOutlined />} size="large" onClick={onReset}>
              Reset
            </Button>
          </Space>
        </Col>
      </Row>
    </Card>
  );
};

export default ReportFilterBar;
